import { useMutation } from '@apollo/client/react'
import { DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useTranslation } from 'react-i18next'
import {
  type Customer,
  ReturnSchedule,
  UpdateCustomerReturnScheduleDocument,
  UpdateCustomerScanCenterDocument,
} from '~app/graph/generated/graphql'
import { getMutationErrorMessage } from '~app/helper/graphql-error'
import useSectionForm, { PartialSaveError } from '~app/hooks/useSectionForm'
import { getReturnsScheduleFromReturnsInterval } from './returns-schedule'
import {
  applyReturnsIntervalChange,
  getReturnsIntervalOptions,
  getScanCenterOptions,
} from './returns-transitions'
import SectionCard from './SectionCard'
import useReturnsConfirmation from './useReturnsConfirmation'

dayjs.extend(utc)

type ReturnsCustomer = Pick<
  Customer,
  'id' | 'returnsInterval' | 'returnsNextAt' | 'scanCenter'
>

const getSectionValues = (customer: ReturnsCustomer) => ({
  returnsInterval: getReturnsScheduleFromReturnsInterval(
    customer.returnsInterval
  ),
  returnsNextAt: (customer.returnsNextAt ?? null) as string | null,
  scanCenter: customer.scanCenter,
})

/*
 * Component
 */

interface Props {
  customer: ReturnsCustomer
  onStateChange: (args: { isDirty: boolean; isSaving: boolean }) => void
}

const OriginalsAndReturnsSection = ({ customer, onStateChange }: Props) => {
  const { t } = useTranslation()
  const { confirmReturnsSave } = useReturnsConfirmation()
  const [updateCustomerScanCenter] = useMutation(
    UpdateCustomerScanCenterDocument
  )
  const [updateCustomerReturnSchedule] = useMutation(
    UpdateCustomerReturnScheduleDocument
  )

  const { baseline, isDirty, isLoading, reset, submit, updateValues, values } =
    useSectionForm({
      errorMessage: t('customerDetails.notifications.saveFailed'),
      onStateChange,
      serverValues: getSectionValues(customer),
      successMessage: t('customerDetails.notifications.returnsSaved'),
      onSubmit: async ({ values }) => {
        const isScanCenterChanged = values.scanCenter !== baseline.scanCenter
        const isScheduleChanged =
          values.returnsInterval !== baseline.returnsInterval ||
          values.returnsNextAt !== baseline.returnsNextAt

        let saved = baseline

        // Scan centre first: updateCustomer recomputes the schedule, so the
        // schedule write has to land after it to survive.
        if (isScanCenterChanged) {
          const { data } = await updateCustomerScanCenter({
            variables: { id: customer.id, scanCenter: values.scanCenter },
          })
          if (data) {
            saved = getSectionValues(data.updateCustomer)
          }
        }

        if (isScheduleChanged) {
          try {
            const { data } = await updateCustomerReturnSchedule({
              variables: {
                data: {
                  returnsInterval: values.returnsInterval,
                  returnsNextAt: values.returnsNextAt,
                },
                where: { id: customer.id },
              },
            })
            if (data) {
              saved = getSectionValues(data.updateCustomerReturnSchedule)
            }
          } catch (error) {
            if (!isScanCenterChanged) {
              throw error
            }
            throw new PartialSaveError({
              message: t('customerDetails.notifications.scheduleNotSaved', {
                reason: getMutationErrorMessage(error),
              }),
              pendingValues: {
                returnsInterval: values.returnsInterval,
                returnsNextAt: values.returnsNextAt,
              },
              savedValues: saved,
            })
          }
        }

        return saved
      },
    })

  const scanCenterOptions = getScanCenterOptions({
    current: baseline.scanCenter,
    getRetiredLabel: ({ scanCenter }) =>
      t('customerDetails.fields.retiredScanCenter', { scanCenter }),
  })

  const isScheduleOff = values.returnsInterval === ReturnSchedule.Off

  return (
    <SectionCard
      isDirty={isDirty}
      isLoading={isLoading}
      onDiscard={reset}
      onSave={async () => {
        if (await confirmReturnsSave({ baseline, values })) {
          await submit()
        }
      }}
      rows={[
        {
          content: (
            <Select
              disabled={isLoading}
              onChange={scanCenter => updateValues({ scanCenter })}
              options={scanCenterOptions}
              style={{ width: 200 }}
              value={values.scanCenter}
            />
          ),
          label: t('misc.scanCenter'),
        },
        {
          content: (
            <Select
              disabled={isLoading}
              onChange={(returnsInterval: ReturnSchedule) =>
                updateValues(
                  applyReturnsIntervalChange({
                    // +1 day: `disabledDate` below rejects today and earlier, so the placeholder must be at least tomorrow
                    earliestNextAt: dayjs
                      .utc()
                      .add(1, 'day')
                      .startOf('day')
                      .toISOString(),
                    returnsInterval,
                    returnsNextAt: values.returnsNextAt,
                  })
                )
              }
              options={getReturnsIntervalOptions({
                current: baseline.returnsInterval,
                getLabel: ({ interval }) =>
                  t(`customerDetails.returnIntervals.${interval}`),
              })}
              style={{ width: 200 }}
              value={values.returnsInterval}
            />
          ),
          label: t('customerDetails.fields.returnsInterval'),
        },
        {
          content: (
            <DatePicker
              allowClear={false}
              disabled={isLoading || isScheduleOff}
              disabledDate={current =>
                current?.isBefore(dayjs().startOf('day').add(1, 'day'))
              }
              format="DD.MM.YYYY"
              onChange={date =>
                updateValues({
                  returnsNextAt: date ? date.toISOString() : null,
                })
              }
              value={
                values.returnsNextAt ? dayjs.utc(values.returnsNextAt) : null
              }
            />
          ),
          label: t('customerDetails.fields.returnsNextAt'),
        },
      ]}
      title={t('customerDetails.sections.originalsAndReturns')}
    />
  )
}

export default OriginalsAndReturnsSection
